
import React from "react";
import { Plan } from "@/types/subscription";
import { ExtendedOrganization } from "@/hooks/organization-settings/types";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlanFeatures } from "./PlanFeatures";
import { useStripeCheckout } from "@/hooks/subscription/useStripeCheckout";

interface PlanCardProps {
  plan: Plan;
  organization: ExtendedOrganization | null;
  onChangePlan: () => void;
  isYearly: boolean;
}

export const PlanCard: React.FC<PlanCardProps> = ({ 
  plan, 
  organization,
  onChangePlan,
  isYearly
}) => {
  const currentPlanId = organization?.subscription_plan_id;
  const isCurrentPlan = currentPlanId === plan.id;
  const { isLoading, hasError, handleCheckout } = useStripeCheckout();

  const displayPrice = isYearly 
    ? (plan.priceYearly / 12).toFixed(2) 
    : plan.price.toFixed(2);
  
  const yearlyTotal = isYearly 
    ? `(${plan.priceYearly}€ par an)` 
    : '';

  const handlePlanChange = async () => {
    if (isCurrentPlan) return;
    
    if (organization?.id) {
      // Directly initiate Stripe checkout instead of redirecting to plans page
      await handleCheckout(plan.id, organization.id, isYearly);
    } else {
      // Fallback to the previous behavior if organization ID is not available
      onChangePlan();
    }
  };

  return (
    <div className={`overflow-hidden rounded-lg border ${
      isCurrentPlan 
        ? 'border-green-500 dark:border-green-600' 
        : 'border-gray-200 dark:border-gray-700'
    } bg-white dark:bg-gray-800 shadow-sm`}>
      {isCurrentPlan && (
        <div className="bg-green-500 dark:bg-green-600 text-white text-center py-1 text-sm font-medium">
          Plan actuel
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-lg font-semibold mb-1">{plan.displayName}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{plan.description}</p>
        
        <div className="my-4 text-center">
          <p className="text-3xl font-bold">
            {displayPrice}€
            <span className="text-base font-normal text-gray-500 dark:text-gray-400"> /mois</span>
          </p>
          {isYearly && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {yearlyTotal}
            </p>
          )}
        </div>
        
        <Button
          onClick={handlePlanChange}
          className="w-full mb-6"
          disabled={isCurrentPlan || isLoading}
          variant={isCurrentPlan ? "outline" : "default"}
        >
          {isLoading 
            ? "Traitement..." 
            : isCurrentPlan 
              ? "Plan actuel" 
              : "Souscrire"}
        </Button>
        
        {hasError && (
          <p className="text-red-500 text-sm mb-4 text-center">
            Erreur lors de l'initialisation du paiement
          </p>
        )}
        
        <PlanFeatures features={plan.features} />
      </div>
    </div>
  );
};
